import { useEffect, useRef, useState } from 'react';
import { ref, getDownloadURL } from 'firebase/storage';
import { Icon } from '../icons';
import { getStorageOrThrow } from '../../lib/firebase';
import { uploadPhotos } from '../../lib/storage';
import type { PhotoRef } from '../../lib/types';

interface Props {
  jobId: string | null;
  photos: PhotoRef[];
  onPhotosUploaded: (next: PhotoRef[]) => Promise<void>;
  onNext: () => void;
  onBack: () => void;
  /** After photos exist the draft project is real — offer return to the library. */
  onBackToProjects?: () => void;
}

export function UploadScreen({
  jobId,
  photos,
  onPhotosUploaded,
  onNext,
  onBack,
  onBackToProjects,
}: Props) {
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [thumbByPath, setThumbByPath] = useState<Record<string, string>>({});
  /** Immediate previews from the user's device (works even if Storage download URLs fail). */
  const [localBlobUrlByPath, setLocalBlobUrlByPath] = useState<Record<string, string>>({});
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const localBlobUrlByPathRef = useRef(localBlobUrlByPath);
  localBlobUrlByPathRef.current = localBlobUrlByPath;

  useEffect(() => {
    setLocalBlobUrlByPath((prev) => {
      const keep = new Set(photos.map((p) => p.path));
      const next = { ...prev };
      for (const path of Object.keys(prev)) {
        if (!keep.has(path)) {
          URL.revokeObjectURL(prev[path]!);
          delete next[path];
        }
      }
      return next;
    });
  }, [photos]);

  useEffect(() => {
    let cancelled = false;
    async function loadThumbs() {
      if (photos.length === 0) {
        if (!cancelled) setThumbByPath({});
        return;
      }
      try {
        const storage = getStorageOrThrow();
        const entries = await Promise.all(
          photos.map(async (p) => {
            try {
              const url = await getDownloadURL(ref(storage, p.path));
              return [p.path, url] as const;
            } catch {
              return [p.path, ''] as const;
            }
          }),
        );
        if (cancelled) return;
        const next: Record<string, string> = {};
        for (const [path, url] of entries) {
          if (url) next[path] = url;
        }
        setThumbByPath(next);
      } catch {
        if (!cancelled) setThumbByPath({});
      }
    }
    void loadThumbs();
    return () => {
      cancelled = true;
    };
  }, [photos]);

  useEffect(
    () => () => {
      for (const u of Object.values(localBlobUrlByPathRef.current)) {
        URL.revokeObjectURL(u);
      }
    },
    [],
  );

  async function handleFiles(list: FileList | File[] | null) {
    if (!jobId) return;
    if (!list || (Array.isArray(list) ? list.length === 0 : list.length === 0)) return;
    const files = Array.isArray(list) ? list : Array.from(list);
    if (photos.length + files.length > 40) {
      setError('עד 40 תמונות בסך הכל');
      return;
    }
    setBusy(true);
    setError(null);
    setProgress(0);
    try {
      const uploaded = await uploadPhotos(jobId, files, photos.length, (loaded, total) => {
        setProgress(total === 0 ? 0 : Math.round((loaded / total) * 100));
      });
      const blobPreviews: Record<string, string> = {};
      files.forEach((file, i) => {
        const ref = uploaded[i];
        if (ref) blobPreviews[ref.path] = URL.createObjectURL(file);
      });
      setLocalBlobUrlByPath((prev) => ({ ...prev, ...blobPreviews }));
      await onPhotosUploaded([...photos, ...uploaded]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'שגיאה בהעלאה');
    } finally {
      setBusy(false);
    }
  }

  function pick() {
    inputRef.current?.click();
  }

  function applyReorder(from: number, to: number) {
    if (from === to || from < 0 || to < 0 || from >= photos.length || to >= photos.length) return;
    const next = [...photos];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    const reindexed = next.map((p, i) => ({ ...p, order: i }));
    void onPhotosUploaded(reindexed);
  }

  return (
    <div className="screen">
      <div className="screen-header">
        <button className="icon-btn" onClick={onBack}>
          <Icon.ChevronRight size={20} />
        </button>
        <div className="step-counter">שלב 2 מתוך 5</div>
        <div style={{ width: 38 }} />
      </div>
      <div className="screen-scroll">
        <div className="eyebrow" style={{ marginTop: 24 }}>
          הזיכרונות
        </div>
        <h1 className="h1">מעלים תמונות</h1>
        <p className="lede">10–40 תמונות זה האידיאל. סדרו אותן כפי שתרצו שיופיעו בסרטון.</p>

        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => {
            void handleFiles(e.target.files);
            e.target.value = '';
          }}
        />

        {photos.length === 0 ? (
          <div
            className={'upload-zone' + (drag ? ' is-drag' : '')}
            onDragOver={(e) => {
              e.preventDefault();
              setDrag(true);
            }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDrag(false);
              void handleFiles(e.dataTransfer.files);
            }}
            onClick={pick}
          >
            <div className="uz-glyph">
              <Icon.Upload size={26} />
            </div>
            <div className="uz-title">{busy ? 'מעלה...' : 'גוררים תמונות לכאן'}</div>
            <div className="uz-sub">
              {busy ? `${progress}%` : (
                <>
                  או לוחצים כדי לבחור מהמכשיר
                  <br />
                  JPG, PNG · עד 40 תמונות
                </>
              )}
            </div>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontFamily: 'Heebo', fontWeight: 600, fontSize: 15 }}>
                {photos.length} תמונות
                <span style={{ fontWeight: 400, color: 'var(--ink-mute)', marginRight: 6 }}>
                  · גררו כרטיסיות לסידור מחדש
                </span>
              </div>
              <button
                onClick={pick}
                disabled={busy}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--accent-deep)',
                  fontFamily: 'Heebo',
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <Icon.Plus size={16} /> {busy ? `${progress}%` : 'הוספה'}
              </button>
            </div>
            <div className="photo-grid">
              {photos.map((p, i) => {
                const thumb = thumbByPath[p.path] || localBlobUrlByPath[p.path];
                const isOver = overIndex === i && dragIndex !== null && dragIndex !== i;
                return (
                  <div
                    key={p.path}
                    className={
                      'photo-tile' +
                      (dragIndex === i ? ' is-dragging' : '') +
                      (isOver ? ' is-drag-over' : '')
                    }
                    draggable={!busy}
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData('text/plain', String(i));
                      setDragIndex(i);
                    }}
                    onDragEnd={() => {
                      setDragIndex(null);
                      setOverIndex(null);
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                      setOverIndex(i);
                    }}
                    onDragLeave={() => setOverIndex((prev) => (prev === i ? null : prev))}
                    onDrop={(e) => {
                      e.preventDefault();
                      const from = Number(e.dataTransfer.getData('text/plain'));
                      if (!Number.isNaN(from)) applyReorder(from, i);
                      setDragIndex(null);
                      setOverIndex(null);
                    }}
                  >
                    {thumb ? (
                      <img
                        className="photo-thumb"
                        src={thumb}
                        alt=""
                        draggable={false}
                        onDragStart={(e) => e.preventDefault()}
                      />
                    ) : (
                      <div className="ph-placeholder">תמונה {i + 1}</div>
                    )}
                    <div className="order-badge">{i + 1}</div>
                  </div>
                );
              })}
              {photos.length < 40 && (
                <div className="photo-tile add" onClick={pick}>
                  <Icon.Plus size={22} />
                </div>
              )}
            </div>
          </>
        )}

        {error && (
          <div
            className="tip-row"
            style={{ background: 'oklch(0.92 0.05 30 / 0.6)', color: 'var(--ink)' }}
          >
            <div>{error}</div>
          </div>
        )}

        <div className="tip-row">
          <div className="tip-glyph">
            <Icon.Lightbulb size={18} />
          </div>
          <div>
            <strong style={{ fontFamily: 'Heebo', fontWeight: 600, color: 'var(--ink)' }}>
              טיפ:
            </strong>{' '}
            בחרו תמונות שמספרות את הסיפור — מילדות, דרך אבני דרך, ועד היום הגדול. מגוון יוצר סרטון
            מרגש יותר.
          </div>
        </div>
      </div>
      <div className="cta-dock">
        {onBackToProjects && photos.length > 0 && (
          <button
            type="button"
            className="btn-secondary"
            style={{ width: '100%', marginBottom: 10 }}
            disabled={busy}
            onClick={onBackToProjects}
          >
            חזור למסך הסרטונים
          </button>
        )}
        <button className="btn-primary" disabled={photos.length < 3 || busy} onClick={onNext}>
          {photos.length < 3 ? `צריך לפחות 3 תמונות` : `המשך לבחירת סגנון`}
          {photos.length >= 3 && <Icon.ArrowLeft size={18} />}
        </button>
      </div>
    </div>
  );
}
